import { apiClient } from './apiClient';
import { Recipe, RecipeFilters } from '@coffeeplexity/shared';

// Create Recipe Data interface for API calls
export interface CreateRecipeData {
  title: string;
  description: string;
  brewing_method: string;
  difficulty: string;
  brewing_parameters: any;
  equipment: string[];
  ingredients: Array<{
    name: string;
    amount: number;
    unit: string;
    notes?: string;
  }>;
  steps: Array<{
    order: number;
    description: string;
    duration?: number;
    temperature?: number;
    notes?: string;
  }>;
  tags: string[];
  images?: string[];
  video_url?: string;
  servings: number;
  prep_time_minutes: number;
  total_time_minutes: number;
  is_public: boolean;
}

export interface RecipeApiResponse {
  recipes: Recipe[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface RateRecipeResponse {
  rating: number;
  averageRating: number;
  ratingCount: number;
}

export const recipeApi = {
  // Get all recipes with filters and pagination
  getRecipes: async (
    params: {
      page?: number;
      limit?: number;
      filters?: Partial<RecipeFilters>;
    } = {}
  ): Promise<RecipeApiResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('size', params.limit.toString());

    // Add filters to query params
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          if (Array.isArray(value) && value.length > 0) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else if (!Array.isArray(value)) {
            queryParams.set(key, value.toString());
          }
        }
      });
    }

    const response = await apiClient.get(`/recipes?${queryParams.toString()}`);

    // Transform backend response to match frontend expectations
    const backendData = response.data;
    return {
      recipes: backendData.recipes,
      pagination: {
        page: backendData.page,
        limit: backendData.per_page,
        total: backendData.total,
        hasMore: backendData.page < backendData.total_pages,
      },
    };
  },

  // Get a single recipe by ID
  getRecipeById: async (id: string): Promise<Recipe> => {
    const response = await apiClient.get(`/recipes/${id}`);
    return response.data;
  },

  // Create a new recipe
  createRecipe: async (data: CreateRecipeData): Promise<Recipe> => {
    const response = await apiClient.post('/recipes', data);
    return response.data;
  },

  // Update an existing recipe
  updateRecipe: async (
    id: string,
    data: Partial<CreateRecipeData>
  ): Promise<Recipe> => {
    const response = await apiClient.put(`/recipes/${id}`, data);
    return response.data;
  },

  // Delete a recipe
  deleteRecipe: async (id: string): Promise<void> => {
    await apiClient.delete(`/recipes/${id}`);
  },

  // Get featured recipes
  getFeaturedRecipes: async (): Promise<Recipe[]> => {
    const response = await apiClient.get('/recipes/featured');
    return response.data;
  },

  // Get recipes by creator
  getRecipesByCreator: async (
    creatorId: string,
    params: {
      page?: number;
      limit?: number;
    } = {}
  ): Promise<RecipeApiResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('size', params.limit.toString());

    const response = await apiClient.get(
      `/creators/${creatorId}/recipes?${queryParams.toString()}`
    );

    // Transform backend response to match frontend expectations
    const backendData = response.data;
    return {
      recipes: backendData.recipes,
      pagination: {
        page: backendData.page,
        limit: backendData.per_page,
        total: backendData.total,
        hasMore: backendData.page < backendData.total_pages,
      },
    };
  },

  // Like a recipe
  likeRecipe: async (
    id: string
  ): Promise<{ liked: boolean; likeCount: number }> => {
    const response = await apiClient.post(`/recipes/${id}/like`, {});
    return response.data;
  },

  // Unlike a recipe
  unlikeRecipe: async (
    id: string
  ): Promise<{ liked: boolean; likeCount: number }> => {
    const response = await apiClient.delete(`/recipes/${id}/like`);
    return response.data;
  },

  // Bookmark a recipe
  bookmarkRecipe: async (id: string): Promise<{ bookmarked: boolean }> => {
    const response = await apiClient.post(`/recipes/${id}/bookmark`, {});
    return response.data;
  },

  // Remove bookmark from recipe
  unbookmarkRecipe: async (id: string): Promise<{ bookmarked: boolean }> => {
    const response = await apiClient.delete(`/recipes/${id}/bookmark`);
    return response.data;
  },

  // Rate a recipe
  rateRecipe: async (
    recipeId: string,
    rating: number
  ): Promise<RateRecipeResponse> => {
    const response = await apiClient.post(`/recipes/${recipeId}/rate`, {
      rating,
    });
    return response.data;
  },

  // Get user's recipes (created by current user)
  // Note: This uses the main recipes endpoint with creator_id filter
  // The creator_id is automatically set to current user on backend when not specified
  getUserRecipes: async (
    params: {
      type?: 'created' | 'bookmarked' | 'liked';
      page?: number;
      limit?: number;
      creator_id?: string;
    } = {}
  ): Promise<RecipeApiResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('size', params.limit.toString());
    if (params.creator_id) queryParams.set('creator_id', params.creator_id);

    // For now, we only support 'created' type via creator_id filter
    // Bookmarked and liked recipes would need separate endpoints
    const response = await apiClient.get(`/recipes?${queryParams.toString()}`);

    // Transform backend response to match frontend expectations
    const backendData = response.data;
    return {
      recipes: backendData.recipes,
      pagination: {
        page: backendData.page,
        limit: backendData.per_page,
        total: backendData.total,
        hasMore: backendData.page < backendData.total_pages,
      },
    };
  },

  // Get recipe categories
  getCategories: async (): Promise<string[]> => {
    const response = await apiClient.get('/recipes/categories');
    return response.data;
  },

  // Get recipe tags
  getTags: async (): Promise<string[]> => {
    const response = await apiClient.get('/recipes/tags');
    return response.data;
  },

  // Search recipes
  searchRecipes: async (params: {
    query: string;
    filters?: Partial<RecipeFilters>;
    page?: number;
    limit?: number;
  }): Promise<RecipeApiResponse & { searchTime: number }> => {
    const queryParams = new URLSearchParams();

    queryParams.set('q', params.query);
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('size', params.limit.toString());

    // Add filters to query params
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          if (Array.isArray(value) && value.length > 0) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else if (!Array.isArray(value)) {
            queryParams.set(key, value.toString());
          }
        }
      });
    }

    const response = await apiClient.get(
      `/recipes/search?${queryParams.toString()}`
    );

    // Transform backend response to match frontend expectations
    const backendData = response.data;
    return {
      recipes: backendData.recipes,
      pagination: {
        page: backendData.page,
        limit: backendData.per_page,
        total: backendData.total,
        hasMore: backendData.page < backendData.total_pages,
      },
      searchTime: backendData.searchTime || 0,
    };
  },
};

export default recipeApi;
